/* Eligible transition selection and interruptible execution; no routing or Shopify loading. */
(function () {
  'use strict';
  const storefront = window.BSStorefront;
  if (!storefront || storefront.shopTransitions) return;
  function registry(rng = Math.random) {
    if (typeof rng !== 'function') throw new TypeError('Expected random source');
    const entries = new Map(); let last = null;
    return {
      register(effect) {
        if (!effect || !/^[a-z][a-z0-9-]*$/.test(effect.id) || typeof effect.play !== 'function' ||
            (effect.eligible != null && typeof effect.eligible !== 'function') || entries.has(effect.id)) throw new Error('Invalid transition');
        entries.set(effect.id, effect);
        return () => { if (entries.get(effect.id) === effect) { entries.delete(effect.id); if (last === effect.id) last = null; } };
      },
      ids: () => [...entries.keys()],
      resolve(id, context) {
        const effect = entries.get(id);
        try { return effect && (!effect.eligible || effect.eligible(context)) ? effect : null; } catch (ignored) { return null; }
      },
      pick(context = {}, forced = null) {
        const eligible = [...entries.values()].filter(effect => { try { return !effect.eligible || effect.eligible(context); } catch (ignored) { return false; } });
        if (!eligible.length) return null;
        let effect = forced && eligible.find(effect => effect.id === forced);
        if (!effect) {
          const pool = eligible.length > 1 ? eligible.filter(effect => effect.id !== last) : eligible;
          const random = Number(rng());
          effect = pool[Number.isFinite(random) ? Math.min(pool.length - 1, Math.max(0, Math.floor(random * pool.length))) : 0];
        }
        last = effect.id; return effect;
      }
    };
  }
  const effects = registry(), active = new WeakMap(), previews = new WeakMap(), diagnostics = new WeakMap();
  function allowed() { return !!storefront.devourGate?.enabled(); }
  function empty() {
    const finished = Promise.resolve({ status: 'skipped' });
    return { finished, ready: () => finished, cancel() {}, dispose() {}, resize() {}, active: false };
  }
  function studioAllowed() {
    const params = new URL(location.href).searchParams.getAll('bs_animation_studio');
    return allowed() && window.Shopify?.theme?.role === 'development' && params.length === 1 && params[0] === '1';
  }
  // Same registered effect and WAAPI objects, with no navigation/readiness adapter.
  function preview(context, id) {
    if (!studioAllowed() || active.has(context.root) || previews.has(context.root) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const actor = storefront.skullActor?.get(context.root), effect = effects.resolve(id, { ...context, actor });
    if (!effect) return null;
    let playback;
    try { playback = effect.play({ ...context, actor, inspection: true }); } catch (ignored) { actor?.interrupt(); return null; }
    if (!playback?.inspection) { playback?.dispose?.(); actor?.interrupt(); return null; }
    let disposed = false;
    const handle = { inspection: playback.inspection, finished: playback.finished,
      cancel() {
        if (disposed) return;
        disposed = true;
        try { playback.dispose(); } finally { if (previews.get(context.root) === handle) previews.delete(context.root); }
      }, dispose() { handle.cancel(); }
    };
    previews.set(context.root, handle);
    Promise.resolve(playback.finished).then(result => { if (result?.status === 'canceled') handle.cancel(); }, () => handle.cancel());
    return handle;
  }
  function create(context) {
    if (!allowed()) return empty();
    const { root } = context;
    previews.get(root)?.cancel();
    active.get(root)?.cancel();
    const actor = storefront.skullActor?.get(root);
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (context.fromHistory || media.matches) { diagnostics.set(root, { effect: null, reason: 'reduced-motion-or-history' }); return empty(); }
    const requested = window.Shopify?.theme?.role === 'development' ? new URL(location.href).searchParams.get('bs_transition') : null;
    const effect = effects.pick({ ...context, actor }, requested);
    if (!effect) { diagnostics.set(root, { effect: null, reason: 'no-eligible-effect' }); return empty(); }
    let playback;
    try { playback = effect.play({ ...context, actor, inspection: false }); } catch (ignored) { actor?.interrupt(); return empty(); }
    if (!playback || typeof playback.finished?.then !== 'function') {
      try { playback?.dispose?.(); } catch (ignored) { /* Invalid effects still release the actor. */ }
      actor?.interrupt(); return empty();
    }
    let released = false, interrupt;
    const interrupted = new Promise(resolve => { interrupt = resolve; });
    function release(cancel) {
      if (released) return;
      released = true;
      interrupt({ status: 'canceled' });
      try { if (cancel) playback.cancel(); } catch (ignored) { /* Release remains mandatory. */ }
      try { playback.dispose(); } catch (ignored) { /* A future effect cannot retain scheduler ownership. */ }
      if (active.get(root) === handle) active.delete(root);
    }
    const finished = Promise.race([playback.finished, interrupted]).catch(() => { release(true); return { status: 'canceled' }; });
    const handle = { finished, active: true,
      async ready() { diagnostic('animation-release-for-ready'); release(true); },
      cancel() { release(true); }, dispose() { release(true); }, resize() { if (!released) playback.resize?.(); }
    };
    active.set(root, handle); diagnostics.set(root, { effect: effect.id, reason: 'selected' });
    finished.then(result => { if (result?.status === 'canceled') release(true); });
    return handle;
  }
  // Opt-in console events only: no URLs, customer data, retained buffer or storage.
  function diagnostic(event, reason = null) {
    if (window.Shopify?.theme?.role !== 'development' || new URL(location.href).searchParams.get('bs_devour_diagnostics') !== '1') return;
    console.debug('[bs-devour]', { event, time: performance.now(), ...(reason ? { reason } : {}) });
  }
  storefront.shopTransitions = { diagnostic, registry, register: effects.register, ids: effects.ids, create, studioAllowed, preview,
    cancel: root => active.get(root)?.cancel(),
    inspect: root => allowed() && window.Shopify?.theme?.role === 'development' ? { ...diagnostics.get(root), state: storefront.skullActor?.get(root)?.state || 'unmounted' } : null
  };
})();
